import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Download, 
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Database,
  Zap,
  Settings,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Globe,
  Server,
  X
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ColumnConfigDropdown, ColumnConfig } from '@/components/ColumnConfigDropdown';
import { Pagination } from '@/components/ui/pagination';
import { apiRequest } from '@/lib/api';

interface DataSyncStat {
  endpoint_name: string;
  total_calls: number;
  successful_calls: number;
  failed_calls: number;
  avg_response_time_ms: number;
  avg_processing_time_ms: number;
  total_records_inserted: number;
  total_records_updated: number;
  last_sync_at: string;
  calls_last_24h: number;
}

interface DataSyncRecord {
  id: string;
  endpoint_name: string;
  endpoint_url: string;
  success: boolean;
  status_code: number;
  data_count: number;
  count_change: number;
  api_response_time_ms: number;
  total_processing_time_ms: number;
  hub_name: string;
  project_name: string;
  error_message: string;
  neon_sync_success: boolean;
  records_inserted: number;
  records_updated: number;
  created_at: string;
  response_data?: any;
  request_payload?: any;
  // 请求详细信息
  request_headers?: Record<string, string>;
  request_url?: string;
  request_method?: string;
  request_query?: Record<string, any>;
  response_headers?: Record<string, string>;
}

interface EndpointTableRow {
  endpoint_name: string;
  endpoint_url: string;
  total_calls: number;
  successful: number;
  failed: number;
  avg_response: number;
  last_sync: string;
  next_sync: string;
}

interface SyncSettings {
  endpoint_name: string;
  interval_value: number;
  interval_unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
  enabled: boolean;
}

interface SyncLog {
  id: string;
  sync_type: string;
  total_synced: number;
  total_updated: number;
  error_count: number;
  started_at: string;
  completed_at: string;
  status: 'completed' | 'failed' | 'running';
  error_message?: string;
}

type TimeUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months';

const AdminDataSyncs: React.FC = () => {
  // Data fetching
  const { data: dataSyncsData, isLoading, error, refetch } = useQuery({
    queryKey: ['/api/admin/data-syncs'],
    queryFn: async () => {
      const data = await apiRequest('/api/admin/data-syncs');
      return data;
    },
    refetchInterval: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
  });

  // Sync logs fetching
  const { data: syncLogsData, isLoading: isLoadingSyncLogs, refetch: refetchSyncLogs } = useQuery({
    queryKey: ['/api/sync-logs'],
    queryFn: async () => {
      const data = await apiRequest('/api/sync-logs');
      return data;
    },
    refetchInterval: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  const stats: DataSyncStat[] = dataSyncsData?.stats || [];
  const recent: DataSyncRecord[] = dataSyncsData?.recent || [];
  const syncLogs: SyncLog[] = syncLogsData || [];

  // State for filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<string>('total_calls');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // State for tabs
  const [activeTab, setActiveTab] = useState<'endpoints' | 'sync-logs'>('endpoints');

  // State for detail panel
  const [selectedEndpoint, setSelectedEndpoint] = useState<string | null>(null);
  const [endpointHistory, setEndpointHistory] = useState<DataSyncRecord[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [expandedRecord, setExpandedRecord] = useState<string | null>(null);

  // State for history filters
  const [historyFilters, setHistoryFilters] = useState({
    dateFrom: '',
    dateTo: '',
    success: 'all' as 'all' | 'true' | 'false'
  });
  const [showHistoryFilters, setShowHistoryFilters] = useState(false);

  // Column configuration
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'endpoint_name', label: 'Endpoint Name', visible: true, sortable: true, width: 'w-48', frozen: true },
    { key: 'endpoint_url', label: 'Endpoint URL', visible: true, sortable: false, width: 'w-32' },
    { key: 'total_calls', label: 'Total Calls', visible: true, sortable: true, width: 'w-24' },
    { key: 'successful', label: 'Successful', visible: true, sortable: true, width: 'w-24' },
    { key: 'failed', label: 'Failed', visible: true, sortable: true, width: 'w-20' },
    { key: 'avg_response', label: 'Avg Response', visible: true, sortable: true, width: 'w-28' },
    { key: 'last_sync', label: 'Last Sync', visible: true, sortable: true, width: 'w-40' },
    { key: 'next_sync', label: 'Next Sync', visible: true, sortable: false, width: 'w-32' },
    { key: 'settings', label: 'Settings', visible: true, sortable: false, width: 'w-24' },
  ]);

  // Sync settings state
  const [syncSettings, setSyncSettings] = useState<SyncSettings[]>([]);
  const [selectedSettingsEndpoint, setSelectedSettingsEndpoint] = useState<string>('');
  const [intervalValue, setIntervalValue] = useState<number>(1);
  const [intervalUnit, setIntervalUnit] = useState<TimeUnit>('hours');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Scrolling refs
  const topScrollRef = React.useRef<HTMLDivElement>(null);
  const bodyScrollRef = React.useRef<HTMLDivElement>(null);

  // Get all defined endpoints
  const getAllEndpoints = (): string[] => {
    return [
      // 🔥 High Priority Endpoints (必须记录)
      'Authentication Token',
      'Get Project Details (Admin)',
      'Get Hub Projects',
      'Get Project Users',
      
      // 🔶 Medium Priority Endpoints (建议记录)
      'Get Hubs List',
      'Get Project Companies',
      'Get Folder Contents',
      'Get Account Projects',
      'Get Account Users',
      'Get Project Files',
      'Get File Versions',
      
      // 🔸 Low Priority Endpoints (可选记录)
      'Get Hub Members',
      'Get Project Activities',
      'Get Company Details',
      'Get User Profile',
      'Get Project Settings',
      'Get Folder Permissions'
    ];
  };

  const getEndpointUrl = (endpointName: string): string => {
    const urlMap: { [key: string]: string } = {
      'Authentication Token': 'https://developer.api.autodesk.com/authentication/v2/token',
      'Get Hubs List': 'https://developer.api.autodesk.com/project/v1/hubs',
      'Get Hub Projects': 'https://developer.api.autodesk.com/project/v1/hubs/{hubId}/projects',
      'Get Project Details (Admin)': 'https://developer.api.autodesk.com/construction/admin/v1/projects/{projectId}',
      'Get Project Users': 'https://developer.api.autodesk.com/construction/admin/v1/projects/{projectId}/users',
      'Get Project Companies': 'https://developer.api.autodesk.com/hq/v1/accounts/{accountId}/projects/{projectId}/companies',
      'Get Folder Contents': 'https://developer.api.autodesk.com/data/v1/projects/{projectId}/folders/{folderId}/contents',
      'Get Account Projects': 'https://developer.api.autodesk.com/construction/admin/v1/accounts/{accountId}/projects',
      'Get Account Users': 'https://developer.api.autodesk.com/construction/admin/v1/accounts/{accountId}/users',
      'Get Project Files': 'https://developer.api.autodesk.com/data/v1/projects/{projectId}/items',
      'Get File Versions': 'https://developer.api.autodesk.com/data/v1/projects/{projectId}/versions/{versionId}',
      'Get Hub Members': 'https://developer.api.autodesk.com/construction/admin/v1/accounts/{accountId}/users',
      'Get Project Activities': 'https://developer.api.autodesk.com/construction/admin/v1/projects/{projectId}/activities',
      'Get Company Details': 'https://developer.api.autodesk.com/hq/v1/accounts/{accountId}/companies/{companyId}',
      'Get User Profile': 'https://developer.api.autodesk.com/userprofile/v1/users/@me',
      'Get Project Settings': 'https://developer.api.autodesk.com/construction/admin/v1/projects/{projectId}/settings',
      'Get Folder Permissions': 'https://developer.api.autodesk.com/data/v1/projects/{projectId}/folders/{folderId}/relationships/refs'
    };
    return urlMap[endpointName] || `https://developer.api.autodesk.com/${endpointName}`;
  };

  const calculateNextSync = (lastSync: string, endpointName?: string): string => {
    const setting = syncSettings.find(s => s.endpoint_name === endpointName);
    if (!setting || !setting.enabled) {
      return 'Manual';
    }
    
    const lastSyncTime = new Date(lastSync);
    const nextSyncTime = new Date(lastSyncTime);
    
    switch (setting.interval_unit) {
      case 'minutes':
        nextSyncTime.setMinutes(nextSyncTime.getMinutes() + setting.interval_value);
        break;
      case 'hours':
        nextSyncTime.setHours(nextSyncTime.getHours() + setting.interval_value);
        break;
      case 'days':
        nextSyncTime.setDate(nextSyncTime.getDate() + setting.interval_value);
        break;
      case 'weeks':
        nextSyncTime.setDate(nextSyncTime.getDate() + (setting.interval_value * 7));
        break;
      case 'months':
        nextSyncTime.setMonth(nextSyncTime.getMonth() + setting.interval_value);
        break;
    }
    
    return nextSyncTime.toLocaleString();
  };

  // Transform data for the table
  const getAllEndpointRows = (): EndpointTableRow[] => {
    // Use stats from API instead of hardcoded list
    const endpointsFromStats = stats.map(stat => stat.endpoint_name);
    const allEndpoints = [...new Set([...getAllEndpoints(), ...endpointsFromStats])];
    
    return allEndpoints.map(endpointName => {
      const stat = stats.find(s => s.endpoint_name === endpointName);
      return {
        endpoint_name: endpointName,
        endpoint_url: getEndpointUrl(endpointName),
        total_calls: Number(stat?.total_calls) || 0,
        successful: Number(stat?.successful_calls) || 0,
        failed: Number(stat?.failed_calls) || 0,
        avg_response: Number(stat?.avg_response_time_ms) || 0,
        last_sync: stat?.last_sync_at || 'Never',
        next_sync: calculateNextSync(stat?.last_sync_at || new Date().toISOString(), endpointName)
      };
    });
  };

  const endpointRows = getAllEndpointRows();

  // Filter and sort data
  const filteredRows = endpointRows.filter(row => {
    const matchesSearch = row.endpoint_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         row.endpoint_url.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && row.total_calls > 0) ||
                         (statusFilter === 'inactive' && row.total_calls === 0) ||
                         (statusFilter === 'errors' && row.failed > 0);

    const matchesPriority = priorityFilter === 'all' ||
                           (priorityFilter === 'high' && ['Authentication Token', 'Get Project Details (Admin)', 'Get Hub Projects', 'Get Project Users'].includes(row.endpoint_name)) ||
                           (priorityFilter === 'medium' && ['Get Hubs List', 'Get Project Companies', 'Get Folder Contents', 'Get Account Projects', 'Get Account Users', 'Get Project Files', 'Get File Versions'].includes(row.endpoint_name)) ||
                           (priorityFilter === 'low' && !['Authentication Token', 'Get Project Details (Admin)', 'Get Hub Projects', 'Get Project Users', 'Get Hubs List', 'Get Project Companies', 'Get Folder Contents', 'Get Account Projects', 'Get Account Users', 'Get Project Files', 'Get File Versions'].includes(row.endpoint_name));

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const sortedRows = [...filteredRows].sort((a, b) => {
    const aValue = a[sortField as keyof EndpointTableRow];
    const bValue = b[sortField as keyof EndpointTableRow];
    
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    
    if (sortDirection === 'asc') {
      return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
    } else {
      return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
    }
  });

  // Pagination
  const totalItems = sortedRows.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRows = sortedRows.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Sync scroll handlers
  const syncFromTop = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    bodyScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  const syncFromBody = () => {
    if (!topScrollRef.current || !bodyScrollRef.current) return;
    topScrollRef.current.scrollLeft = bodyScrollRef.current.scrollLeft;
    setIsScrolled(bodyScrollRef.current.scrollLeft > 0);
  };

  // Hide top scrollbar CSS
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'hide-top-scrollbar-data-syncs';
    style.textContent = `
      .hide-top-scrollbar-data-syncs::-webkit-scrollbar {
        display: none;
      }
    `;
    
    const existingStyle = document.getElementById('hide-top-scrollbar-data-syncs');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    document.head.appendChild(style);
    
    return () => {
      const styleToRemove = document.getElementById('hide-top-scrollbar-data-syncs');
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, []);

  // Sync settings functions
  const loadSyncSettings = async () => {
    try {
      const response = await apiRequest('/api/admin/sync-settings');
      if (response.success) {
        setSyncSettings(response.settings.map((setting: any) => ({
          endpoint_name: setting.endpoint_name,
          interval_value: setting.interval_value,
          interval_unit: setting.interval_unit,
          enabled: setting.enabled
        })));
      }
    } catch (error) {
      console.error('Failed to load sync settings:', error);
      // Fallback to localStorage for backward compatibility
      try {
        const saved = localStorage.getItem('data-sync-settings');
        if (saved) {
          setSyncSettings(JSON.parse(saved));
        }
      } catch (fallbackError) {
        console.error('Fallback load failed:', fallbackError);
      }
    }
  };

  const handleSaveSettings = async () => {
    if (!selectedSettingsEndpoint) return;

    const setting = {
      interval_value: intervalValue,
      interval_unit: intervalUnit,
      enabled: true,
      description: `Sync interval for ${selectedSettingsEndpoint}`
    };

    try {
      const response = await apiRequest(
        `/api/admin/sync-settings/${encodeURIComponent(selectedSettingsEndpoint)}`, 
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(setting)
        }
      );
      
      if (response.success) {
        // Refresh settings from server
        await loadSyncSettings();
        
        setIsSettingsOpen(false);
        setSelectedSettingsEndpoint('');
        setIntervalValue(1);
        setIntervalUnit('hours');
        
        console.log('✅ Sync setting saved successfully');
      } else {
        console.error('❌ Failed to save sync setting:', response.error);
      }
    } catch (error) {
      console.error('❌ Failed to save sync settings:', error);
      
      // Fallback to localStorage
      const newSettings = [...syncSettings];
      const existingIndex = newSettings.findIndex(s => s.endpoint_name === selectedSettingsEndpoint);
      
      const localSetting: SyncSettings = {
        endpoint_name: selectedSettingsEndpoint,
        interval_value: intervalValue,
        interval_unit: intervalUnit,
        enabled: true
      };

      if (existingIndex >= 0) {
        newSettings[existingIndex] = localSetting;
      } else {
        newSettings.push(localSetting);
      }

      setSyncSettings(newSettings);
      localStorage.setItem('data-sync-settings', JSON.stringify(newSettings));
      
      setIsSettingsOpen(false);
      setSelectedSettingsEndpoint('');
      setIntervalValue(1);
      setIntervalUnit('hours');
    }
  };

  useEffect(() => {
    loadSyncSettings();
  }, []);

  // Fetch endpoint history
  const fetchEndpointHistory = async (endpointName: string, filters?: any) => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    setEndpointHistory([]); // Clear previous data immediately
    
    try {
      // 构建查询参数
      const queryParams = new URLSearchParams();
      const currentFilters = filters || historyFilters;
      
      if (currentFilters.dateFrom) {
        queryParams.append('dateFrom', currentFilters.dateFrom);
      }
      if (currentFilters.dateTo) {
        queryParams.append('dateTo', currentFilters.dateTo);
      }
      if (currentFilters.success !== 'all') {
        queryParams.append('success', currentFilters.success);
      }
      
      const queryString = queryParams.toString();
      const url = `/api/admin/data-syncs/endpoint/${encodeURIComponent(endpointName)}${queryString ? `?${queryString}` : ''}`;
      
      const data = await apiRequest(url);
      setEndpointHistory(data.history || []);
    } catch (error) {
      console.error('Failed to fetch endpoint history:', error);
      setHistoryError('Failed to load endpoint history. Please try again.');
      setEndpointHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleRowClick = (endpointName: string) => {
    setSelectedEndpoint(endpointName);
    fetchEndpointHistory(endpointName);
  };

  const handleDownloadDataset = async () => {
    try {
      console.log('📦 Starting dataset download...');
      
      // Show loading state
      // You can add a toast notification here if you have toast system
      
      // Make API call to download dataset
      const response = await fetch('/api/admin/download-dataset', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `hg-acc-dataset-${new Date().toISOString().split('T')[0]}.zip`;
      
      // Trigger download
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      console.log('✅ Dataset download completed');
      
    } catch (error) {
      console.error('❌ Error downloading dataset:', error);
      // You can add error toast notification here
    }
  };
  
  const handleCloseDetail = () => {
    setSelectedEndpoint(null);
    setEndpointHistory([]);
    setIsLoadingHistory(false);
    setHistoryError(null);
    setExpandedRecord(null);
    // Reset filters when closing
    setHistoryFilters({
      dateFrom: '',
      dateTo: '',
      success: 'all'
    });
    setShowHistoryFilters(false);
  };

  // Handle filter changes
  const handleFilterChange = (filterType: string, value: string) => {
    const newFilters = { ...historyFilters, [filterType]: value };
    setHistoryFilters(newFilters);
  };

  const applyFilters = () => {
    if (selectedEndpoint) {
      fetchEndpointHistory(selectedEndpoint, historyFilters);
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      dateFrom: '',
      dateTo: '',
      success: 'all' as 'all' | 'true' | 'false'
    };
    setHistoryFilters(clearedFilters);
    if (selectedEndpoint) {
      fetchEndpointHistory(selectedEndpoint, clearedFilters);
    }
  };
  
  const renderJsonData = (data: any, title: string) => {
    if (!data) return null;
    
    return (
      <div className="mt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">{title}:</span>
        </div>
        <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
          <pre className="text-xs text-gray-800 whitespace-pre-wrap">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const visibleColumns = columns.filter(col => col.visible);

  // Render sync logs
  const renderSyncLogs = () => {
    if (isLoadingSyncLogs) {
      return (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading sync logs...</span>
        </div>
      );
    }

    if (syncLogs.length === 0) {
      return (
        <div className="text-center py-8">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No sync logs available</h3>
          <p className="text-gray-600">Sync logs will appear here once automatic syncs start running.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {syncLogs.map((log) => (
          <Card key={log.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {log.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : log.status === 'failed' ? (
                    <XCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />
                  )}
                  <div>
                    <h4 className="font-semibold text-gray-900 capitalize">
                      {log.sync_type.replace('_', ' ')} Sync
                    </h4>
                    <p className="text-sm text-gray-600">
                      {new Date(log.started_at).toLocaleString()} - {new Date(log.completed_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Badge variant={log.status === 'completed' ? 'default' : log.status === 'failed' ? 'destructive' : 'secondary'}>
                  {log.status}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{log.total_synced}</p>
                  <p className="text-sm text-gray-600">New Records</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{log.total_updated}</p>
                  <p className="text-sm text-gray-600">Updated Records</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-600">{log.error_count}</p>
                  <p className="text-sm text-gray-600">Errors</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600">
                    {Math.round((new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()) / 1000)}s
                  </p>
                  <p className="text-sm text-gray-600">Duration</p>
                </div>
              </div>
              
              {log.error_message && (
                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                  <p className="text-sm text-red-700">
                    <span className="font-medium">Error:</span> {log.error_message}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderCell = (row: EndpointTableRow, columnKey: string) => {
    switch (columnKey) {
      case 'endpoint_name':
        return (
          <div className="font-medium text-gray-900">
            {row.endpoint_name}
          </div>
        );
      case 'endpoint_url':
        return (
          <div 
            className="text-sm text-muted-foreground font-mono truncate cursor-pointer hover:text-blue-600 max-w-32"
            title={row.endpoint_url}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(row.endpoint_url);
              alert('URL copied to clipboard!');
            }}
          >
            {row.endpoint_url}
          </div>
        );
      case 'total_calls':
        return (
          <button 
            className="font-mono text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.endpoint_name);
            }}
          >
            {row.total_calls}
          </button>
        );
      case 'successful':
        return (
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            <span className="font-mono">{row.successful}</span>
          </div>
        );
      case 'failed':
        return row.failed > 0 ? (
          <div className="flex items-center">
            <XCircle className="w-4 h-4 text-red-500 mr-1" />
            <span className="font-mono text-red-600">{row.failed}</span>
          </div>
        ) : (
          <span className="font-mono text-gray-400">0</span>
        );
      case 'avg_response':
        return <span className="font-mono">{(row.avg_response || 0).toFixed(0)}ms</span>;
      case 'last_sync':
        return row.last_sync === 'Never' ? (
          <Badge variant="secondary">Never</Badge>
        ) : (
          <span className="text-sm">{new Date(row.last_sync).toLocaleString()}</span>
        );
      case 'next_sync':
        return row.next_sync === 'Manual' ? (
          <Badge variant="outline">Manual</Badge>
        ) : (
          <span className="text-sm">{row.next_sync}</span>
        );
      case 'settings':
        return (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedSettingsEndpoint(row.endpoint_name);
              const setting = syncSettings.find(s => s.endpoint_name === row.endpoint_name);
              if (setting) {
                setIntervalValue(setting.interval_value);
                setIntervalUnit(setting.interval_unit);
              }
              setIsSettingsOpen(true);
            }}
          >
            <Settings className="h-4 w-4" />
          </Button>
        );
      default:
        return null;
    }
  };

  const exportToCSV = () => {
    const headers = visibleColumns.map(col => col.label);
    const csvContent = [
      headers.join(','),
      ...paginatedRows.map(row => 
        visibleColumns.map(col => {
          const value = row[col.key as keyof EndpointTableRow];
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `data-syncs-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          <span className="ml-2 text-gray-600">Loading sync data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <Card>
          <CardContent className="p-6">
            <div className="text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to load sync data</h3>
              <p className="text-gray-600 mb-4">There was an error loading the data synchronization information.</p>
              <Button
                onClick={() => refetch()}
                className="mt-4"
              >
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Calculate stats for cards - only use stats with actual data
  const activeStats = stats.filter(stat => Number(stat.total_calls) > 0);
  const totalEndpoints = stats.length;
  const activeEndpoints = activeStats.length;
  const totalCalls = stats.reduce((sum, stat) => sum + Number(stat.total_calls), 0);
  const totalErrors = stats.reduce((sum, stat) => sum + Number(stat.failed_calls), 0);

  return (
    <div className="space-y-3">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Database className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Endpoints</p>
                <p className="text-2xl font-bold text-gray-900">{totalEndpoints}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Endpoints</p>
                <p className="text-2xl font-bold text-gray-900">{activeEndpoints}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Calls</p>
                <p className="text-2xl font-bold text-gray-900">{totalCalls.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <XCircle className="w-8 h-8 text-red-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Errors</p>
                <p className="text-2xl font-bold text-gray-900">{totalErrors.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="p-0">
          <div className="flex border-b">
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'endpoints'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('endpoints')}
            >
              <Database className="w-4 h-4 inline mr-2" />
              API Endpoints
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'sync-logs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('sync-logs')}
            >
              <Activity className="w-4 h-4 inline mr-2" />
              Sync Logs
              {syncLogs.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {syncLogs.length}
                </Badge>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Tab Content */}
      {activeTab === 'endpoints' && (
        <>
          {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search endpoints..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="errors">With Errors</SelectItem>
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priority</SelectItem>
                  <SelectItem value="high">High Priority</SelectItem>
                  <SelectItem value="medium">Medium Priority</SelectItem>
                  <SelectItem value="low">Low Priority</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="overflow-hidden" style={{marginBottom: '8px'}}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl font-bold text-gray-900">Data Syncs</CardTitle>
              <p className="text-sm text-gray-600 mt-1">Monitor ACC API sync status</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2"
              >
                <Search className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              
              <ColumnConfigDropdown 
                columns={columns}
                onColumnsChange={setColumns}
              />
              
              <Button variant="outline" size="sm" onClick={exportToCSV}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              
              <Button
                size="sm"
                onClick={() => handleDownloadDataset()}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Dataset
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Top scrollbar */}
          <div
            ref={topScrollRef}
            onScroll={syncFromTop}
            className="w-full overflow-x-auto hide-top-scrollbar-data-syncs"
            style={{ 
              height: 0,
              scrollbarGutter: 'stable',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            <div style={{ width: '1400px', height: 1 }} />
          </div>

          {/* Main table container */}
          <div
            ref={bodyScrollRef}
            className="overflow-x-auto overflow-y-scroll"
            style={{
              height: 'calc(100vh - 160px)',
              maxWidth: '100%',
            }}
            onScroll={syncFromBody}
          >
            <Table className="border-collapse" style={{ minWidth: '1400px', width: '1400px' }}>
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead 
                      key={column.key} 
                      className={`${column.width} py-2${column.sortable ? ' cursor-pointer hover:bg-gray-50' : ''}${column.frozen ? ' sticky left-0 z-40 bg-gray-50 shadow-lg' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}
                      onClick={() => column.sortable && handleSort(column.key)}
                    >
                      <div className="flex items-center gap-2">
                        <span>{column.label}</span>
                        {column.sortable && sortField === column.key && (
                          sortDirection === 'asc' ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedRows.map((row, index) => (
                  <TableRow 
                    key={`${row.endpoint_name}-${index}`} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(row.endpoint_name)}
                  >
                    {visibleColumns.map((column) => (
                      <TableCell key={column.key} className={`${column.width} py-2${column.frozen ? ' sticky left-0 z-20 bg-white shadow-lg hover:bg-white' : ''}${column.frozen && isScrolled ? ' border-r border-gray-300' : ''}`}>
                        {renderCell(row, column.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          <div className="bg-white">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </CardContent>
      </Card>

      {/* Right Side Detail Panel */}
      {selectedEndpoint && (
        <div className="fixed inset-y-0 right-0 w-1/3 bg-white shadow-lg border-l z-50 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{selectedEndpoint} History</h3>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowHistoryFilters(!showHistoryFilters)}
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Filters
                </Button>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleCloseDetail}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Filters Panel */}
            {showHistoryFilters && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                <h4 className="text-sm font-medium mb-3">Filter Call History</h4>
                <div className="space-y-3">
                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="dateFrom" className="text-xs">From Date</Label>
                      <Input
                        id="dateFrom"
                        type="date"
                        value={historyFilters.dateFrom}
                        onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label htmlFor="dateTo" className="text-xs">To Date</Label>
                      <Input
                        id="dateTo"
                        type="date"
                        value={historyFilters.dateTo}
                        onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>
                  
                  {/* Success Filter */}
                  <div>
                    <Label htmlFor="successFilter" className="text-xs">Status</Label>
                    <Select 
                      value={historyFilters.success} 
                      onValueChange={(value) => handleFilterChange('success', value)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="true">✅ Success Only</SelectItem>
                        <SelectItem value="false">❌ Failed Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Filter Actions */}
                  <div className="flex items-center space-x-2 pt-2">
                    <Button 
                      size="sm" 
                      onClick={applyFilters}
                      className="h-7 text-xs"
                    >
                      Apply Filters
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearFilters}
                      className="h-7 text-xs"
                    >
                      Clear
                    </Button>
                    <div className="text-xs text-gray-500 ml-auto">
                      {historyFilters.dateFrom || historyFilters.dateTo || historyFilters.success !== 'all' 
                        ? `${endpointHistory.length} filtered results` 
                        : `${endpointHistory.length} total results`}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {isLoadingHistory ? (
              <div className="text-center py-8">
                <RefreshCw className="mx-auto h-8 w-8 text-blue-500 animate-spin mb-4" />
                <p className="text-gray-600 font-medium">Loading endpoint history...</p>
                <p className="text-gray-400 text-sm mt-2">Please wait while we fetch the data</p>
              </div>
            ) : historyError ? (
              <div className="text-center py-8">
                <XCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                <p className="text-red-600 font-medium mb-2">Error Loading History</p>
                <p className="text-gray-500 text-sm mb-4">{historyError}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => fetchEndpointHistory(selectedEndpoint!)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            ) : endpointHistory.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <p className="text-gray-500">No sync history available</p>
                <p className="text-gray-400 text-sm mt-2">This endpoint hasn't been called yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {endpointHistory.map((record, index) => (
                  <div key={record.id || index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        {record.success ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-sm font-medium">
                          {record.success ? 'Success' : 'Failed'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(record.created_at).toLocaleString()}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Status Code:</span> {record.status_code}
                      </div>
                      <div>
                        <span className="font-medium">Response Time:</span> {record.api_response_time_ms}ms
                      </div>
                      <div>
                        <span className="font-medium">Data Count:</span> {record.data_count}
                      </div>
                      <div>
                        <span className="font-medium">Count Change:</span> 
                        <span className={record.count_change > 0 ? 'text-green-600' : record.count_change < 0 ? 'text-red-600' : 'text-gray-600'}>
                          {record.count_change > 0 ? '+' : ''}{record.count_change}
                        </span>
                      </div>
                    </div>
                    
                    {record.hub_name && (
                      <div className="mt-2 text-sm">
                        <span className="font-medium">Hub:</span> {record.hub_name}
                        {record.project_name && (
                          <span className="ml-2">
                            <span className="font-medium">Project:</span> {record.project_name}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {record.error_message && (
                      <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-700">
                        <span className="font-medium">Error:</span> {record.error_message}
                      </div>
                    )}
                    
                    {/* Details Toggle - Always show for better UX */}
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setExpandedRecord(expandedRecord === record.id ? null : record.id)}
                        className="text-xs"
                      >
                        {expandedRecord === record.id ? 'Hide' : 'Show'} Details
                        {(record.response_data || record.request_payload || record.request_headers || record.response_headers) && (
                          <span className="ml-1 text-green-600 text-xs">📊</span>
                        )}
                        <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${
                          expandedRecord === record.id ? 'rotate-180' : ''
                        }`} />
                      </Button>
                    </div>
                    
                    {/* Expanded Details */}
                    {expandedRecord === record.id && (
                      <div className="mt-3 space-y-3">
                        {record.response_data ? (
                          renderJsonData(record.response_data, '📊 API Response')
                        ) : (
                          <div className="p-3 bg-yellow-50 rounded border border-yellow-200">
                            <p className="text-sm text-yellow-700 flex items-center">
                              <span className="mr-2">📋</span>
                              No response data available for this sync record
                            </p>
                            <p className="text-xs text-yellow-600 mt-1">
                              This may be an older record before JSON logging was implemented
                            </p>
                          </div>
                        )}
                        
                        {record.request_payload ? (
                          renderJsonData(record.request_payload, '📤 Request Payload')
                        ) : (
                          <div className="p-2 bg-gray-50 rounded border">
                            <p className="text-xs text-gray-600 flex items-center">
                              <span className="mr-2">📤</span>
                              No request payload data
                            </p>
                          </div>
                        )}
                        
                        {/* Request Details Section */}
                        <div className="p-3 bg-indigo-50 rounded border border-indigo-200">
                          <h6 className="text-sm font-medium text-indigo-800 mb-2 flex items-center">
                            <span className="mr-2">🔗</span>
                            Request Details
                          </h6>
                          <div className="space-y-2 text-xs">
                            {record.request_url && (
                              <div>
                                <span className="text-indigo-600 font-medium">URL:</span>
                                <div className="mt-1 p-2 bg-white rounded border text-xs font-mono break-all">
                                  {record.request_url}
                                </div>
                              </div>
                            )}
                            {record.request_method && (
                              <div>
                                <span className="text-indigo-600 font-medium">Method:</span> 
                                <span className="ml-1 px-2 py-1 bg-indigo-100 text-indigo-800 rounded text-xs font-mono">
                                  {record.request_method}
                                </span>
                              </div>
                            )}
                            {record.request_headers && (
                              <div>
                                <span className="text-indigo-600 font-medium">Request Headers:</span>
                                <pre className="mt-1 p-2 bg-white rounded border text-xs font-mono overflow-x-auto">
                                  {JSON.stringify(record.request_headers, null, 2)}
                                </pre>
                              </div>
                            )}
                            {record.request_query && typeof record.request_query === 'object' && Object.keys(record.request_query).length > 0 && (
                              <div>
                                <span className="text-indigo-600 font-medium">Query Parameters:</span>
                                <pre className="mt-1 p-2 bg-white rounded border text-xs font-mono overflow-x-auto">
                                  {JSON.stringify(record.request_query, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Response Details Section */}
                        {record.response_headers && (
                          <div className="p-3 bg-green-50 rounded border border-green-200">
                            <h6 className="text-sm font-medium text-green-800 mb-2 flex items-center">
                              <span className="mr-2">📝</span>
                              Response Headers
                            </h6>
                            <pre className="p-2 bg-white rounded border text-xs font-mono overflow-x-auto">
                              {JSON.stringify(record.response_headers, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        {/* Additional sync details */}
                        <div className="p-3 bg-blue-50 rounded border border-blue-200">
                          <h6 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
                            <span className="mr-2">📈</span>
                            Sync Performance
                          </h6>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-blue-600 font-medium">API Response:</span> {record.api_response_time_ms || 0}ms
                            </div>
                            <div>
                              <span className="text-blue-600 font-medium">Total Processing:</span> {record.total_processing_time_ms || 0}ms
                            </div>
                            <div>
                              <span className="text-blue-600 font-medium">Records Inserted:</span> {record.records_inserted || 0}
                            </div>
                            <div>
                              <span className="text-blue-600 font-medium">Records Updated:</span> {record.records_updated || 0}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Neon Sync: {record.neon_sync_success ? (
                          <span className="text-green-600">Success</span>
                        ) : (
                          <span className="text-red-600">Failed</span>
                        )}
                      </span>
                      <span>
                        Records: +{record.records_inserted} / ~{record.records_updated}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sync Logs Tab Content */}
      {activeTab === 'sync-logs' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-2xl font-bold text-gray-900">Sync Logs</CardTitle>
                <p className="text-sm text-gray-600 mt-1">Monitor automatic sync operations</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchSyncLogs()}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {renderSyncLogs()}
          </CardContent>
        </Card>
      )}
        </>
      )}

      {/* Sync Settings Dialog */}
      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Configure Sync Settings</DialogTitle>
            <DialogDescription>
              Set automatic sync intervals for endpoints.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="endpoint" className="text-right">
                Endpoint
              </Label>
              <Select value={selectedSettingsEndpoint} onValueChange={setSelectedSettingsEndpoint}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select endpoint" />
                </SelectTrigger>
                <SelectContent>
                  {getAllEndpoints().map(endpoint => (
                    <SelectItem key={endpoint} value={endpoint}>{endpoint}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="interval" className="text-right">
                Interval
              </Label>
              <Input
                id="interval"
                type="number"
                min="1"
                value={intervalValue}
                onChange={(e) => setIntervalValue(parseInt(e.target.value) || 1)}
                className="col-span-1"
              />
              <Select value={intervalUnit} onValueChange={(value: TimeUnit) => setIntervalUnit(value)}>
                <SelectTrigger className="col-span-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutes</SelectItem>
                  <SelectItem value="hours">Hours</SelectItem>
                  <SelectItem value="days">Days</SelectItem>
                  <SelectItem value="weeks">Weeks</SelectItem>
                  <SelectItem value="months">Months</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" onClick={handleSaveSettings} disabled={!selectedSettingsEndpoint}>
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDataSyncs;